import { ActivatedRouteSnapshot, NavigationEnd, Resolve, Router, RouterStateSnapshot } from '@angular/router'
import { Injectable } from '@angular/core'
import { combineLatest, Observable, of } from 'rxjs'
import {
    BaseDealConfigurationFieldsFragment,
    ConfigurationModel,
    ConfigurationModelsForCheckoutGQL,
    ConfigurationModelsForCheckoutQuery,
    CreateDealGQL,
    CreateDealMutation,
    DealByModelIdGQL,
    DealByModelIdQuery,
    DealConfigurationGQL,
    DealInput,
    DealLocale,
    Status,
} from '../../../../../../generated/graphql'
import { Location } from '@angular/common'
import { catchError, filter, map, mapTo, mergeMap, pluck, take, tap } from 'rxjs/operators'
import { ApolloQueryResult } from '@apollo/client/core'
import { UnconnectedCheckoutService } from '../../../services/unconnected-checkout.service'
import { AuthenticationService } from '../../../../../../framework/authentication/services/authentication.service'
import { DealQueryService } from 'modules/deals/services/dealQuery.service'
import { Apollo } from 'apollo-angular'
import { DealMutationService } from '../../../../services/dealMutation.service'
import { ErrorService } from '../../../../../../ui/monolith/services/error.service'
import { LocaleService } from '../../../../../../framework/locale/services/locale.service'

@Injectable()
export class ProductConfigurationIdResolver implements Resolve<string> {
    constructor(
        private createDealGQL: CreateDealGQL,
        private unconnectedCheckoutService: UnconnectedCheckoutService,
        private dealQueryService: DealQueryService,
        private authenticationService: AuthenticationService,
        private location: Location,
        private router: Router,
        private dealByModelIdGQL: DealByModelIdGQL,
        private apollo: Apollo,
        private dealConfigurationGQL: DealConfigurationGQL,
        private dealMutationService: DealMutationService,
        private configurationModelsForCheckoutGQL: ConfigurationModelsForCheckoutGQL,
        private errorService: ErrorService,
        private localeService: LocaleService
    ) {}

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<string> {
        const modelId = route.params.modelId || route.data.modelId || route.parent.data.modelId
        const editionId = route.params.editionId || route.data.editionId || route.parent.data.editionId

        return of({ modelId, editionId }).pipe(
            // Get a default model and edition ID if either is currently undefined. Otherwise, pass on the values we got.
            mergeMap<{ modelId: string; editionId: string }, Observable<{ modelId: string; editionId: string }>>(
                ({ modelId, editionId }) => {
                    if (!modelId || !editionId) {
                        return this.getDefaultModelAndEditionId(modelId)
                    }

                    return of({ modelId, editionId })
                }
            ),

            // Get deal for given modelId and editionId, tailored to whether the user is logged in or not
            mergeMap<{ modelId: string; editionId: string }, Observable<BaseDealConfigurationFieldsFragment>>(
                ({ modelId, editionId }) => {
                    if (this.authenticationService.isLoggedIn) {
                        return this.dealByModelIdGQL.fetch({ modelId }).pipe(
                            pluck<ApolloQueryResult<DealByModelIdQuery>, BaseDealConfigurationFieldsFragment>(
                                'data',
                                'DealByModelId'
                            ),
                            mergeMap(deal => {
                                // only use deal if in configuration state, otherwise create a new one
                                if (deal?.id && deal?.status === Status.Configuration) {
                                    return of<BaseDealConfigurationFieldsFragment>(deal)
                                } else {
                                    return this.createConfiguration(modelId, editionId)
                                }
                            }),
                            // create new deal if server errors while getting open deals based on modelId
                            catchError(() => this.createConfiguration(modelId, editionId))
                        )
                    } else {
                        const unconnectedCheckoutId = this.unconnectedCheckoutService.getCheckoutIdByMetaData(
                            'modelId',
                            modelId
                        )

                        // test if there is an checkout id in localstorage, if not, create a new one
                        if (unconnectedCheckoutId) {
                            return <Observable<BaseDealConfigurationFieldsFragment>>(
                                this.dealQueryService
                                    .fetchDealConfiguration(unconnectedCheckoutId)
                                    .pipe(catchError(() => this.createConfiguration(modelId, editionId)))
                            )
                        } else {
                            return this.createConfiguration(modelId, editionId)
                        }
                    }
                }
            ),

            // check if queryparameter has name of option, if so, overwrite options
            mergeMap<BaseDealConfigurationFieldsFragment, Observable<BaseDealConfigurationFieldsFragment>>(deal => {
                const optionsWithQueryparameters = deal.options.filter(option => {
                    const optionKey = option.__typename === 'BooleanDealOption' ? option.key : option.name
                    return route.queryParams?.[optionKey]
                })

                if (!optionsWithQueryparameters.length) {
                    // cache result from configuration creation so it will load instantly
                    this.apollo.getClient().writeQuery({
                        variables: { id: deal.id },
                        data: {
                            Deal: deal,
                        },
                        query: this.dealConfigurationGQL.document,
                    })
                    return of(deal)
                } else {
                    const updateMutations = optionsWithQueryparameters.map(option => {
                        const optionKey = option.__typename === 'BooleanDealOption' ? option.key : option.name
                        let optionValue = route.queryParams[optionKey]
                        optionValue = optionValue === 'true' ? true : optionValue
                        optionValue = optionValue === 'false' ? false : optionValue
                        return this.dealMutationService.updateDealOption(deal.id, option, optionValue)
                    })

                    this.router.events
                        .pipe(
                            filter(event => event instanceof NavigationEnd),
                            take(1)
                        )
                        .subscribe(() => {
                            const locationPath = this.location.path()
                            let newLocation = locationPath

                            optionsWithQueryparameters.forEach(option => {
                                const optionKey = option.__typename === 'BooleanDealOption' ? option.key : option.name
                                const value = route.queryParams[optionKey]
                                if (optionKey) {
                                    newLocation = newLocation.replace(`${optionKey}=${value}&`, '')
                                    newLocation = newLocation.replace(`${optionKey}=${value}`, '')
                                }
                            })

                            if (newLocation.endsWith('?')) {
                                newLocation = newLocation.slice(0, -1)
                            }

                            if (newLocation !== locationPath) {
                                this.location.replaceState(newLocation)
                            }
                        })

                    return combineLatest(updateMutations).pipe(
                        take(1),
                        mapTo(deal),
                        catchError(() => of(deal))
                    )
                }
            }),

            // return id of deal
            pluck<BaseDealConfigurationFieldsFragment, string>('id')
        )
    }

    // If modelId is undefined or not a valid ID, get the first model. If it isn't, get the correct model.
    // Get the ID for the most affordable edition within the model.
    private getDefaultModelAndEditionId(modelId): Observable<{ modelId: string; editionId: string }> {
        return this.configurationModelsForCheckoutGQL.fetch().pipe(
            pluck<ApolloQueryResult<ConfigurationModelsForCheckoutQuery>, ConfigurationModel[]>(
                'data',
                'ConfigurationModels'
            ),
            map<ConfigurationModel[], { modelId: string; editionId: string }>(models => {
                const model = models.find(({ id }) => id === modelId) || models[0]
                const edition = [...model.editions].sort(
                    (editionA, editionB) => editionA.fiscalValue - editionB.fiscalValue
                )[0]
                return { modelId: model.id, editionId: edition.id }
            })
        )
    }

    private createConfiguration(
        modelId,
        editionId,
        allowRetry: boolean = true
    ): Observable<BaseDealConfigurationFieldsFragment> {
        const input: DealInput = { editionId }
        const locale = this.localeService.locale

        if (locale) {
            input.locale = locale.replace('-', '_') as DealLocale
        }

        return this.createDealGQL.mutate({ input }).pipe(
            pluck<ApolloQueryResult<CreateDealMutation>, BaseDealConfigurationFieldsFragment>('data', 'createDeal'),
            tap(({ id }) => {
                if (!this.authenticationService.isLoggedIn) {
                    this.unconnectedCheckoutService.storeCheckoutIdByMetaData('modelId', modelId, id)
                }
            }),

            allowRetry
                ? // Catch cases where a modelId and an editionId is set, but either of them is invalid.
                  catchError(() => {
                      return this.getDefaultModelAndEditionId(modelId).pipe(
                          mergeMap(({ modelId, editionId }) => this.createConfiguration(modelId, editionId, false))
                      )
                  })
                : // show error message if everything fails.
                  this.errorService.observableCatcher()
        )
    }
}
